/**
 * challenges.ts — opt-in run modifiers. Starting one applies a penalty modifier;
 * earning the target money while it's active grants a permanent reward (recorded
 * in challenges.completed and contributed via visitSources). No RNG, no reset.
 */
import type { GameState } from '../core/GameState';
import type { EventBus } from '../core/EventBus';
import { CHALLENGE_BY_ID } from '../data';
import { meetsRequirement } from './unlock';
import { addModifier } from './modifiers';

const MOD_ID = (id: string) => `challenge:${id}`;

export function challengeUnlocked(state: GameState, id: string): boolean {
  const c = CHALLENGE_BY_ID[id];
  return !!c && meetsRequirement(state, c.requires);
}
export function challengeProgress(state: GameState): number {
  if (!state.challenges.activeId) return 0;
  return Math.max(0, state.stats.lifetimeEarnings - state.challenges.startLife);
}

export function startChallenge(state: GameState, id: string, bus?: EventBus): boolean {
  const c = CHALLENGE_BY_ID[id];
  if (!c || state.challenges.activeId || state.challenges.completed.includes(id)) return false;
  if (!challengeUnlocked(state, id)) return false;
  state.challenges.activeId = id;
  state.challenges.startLife = state.stats.lifetimeEarnings;
  addModifier(state, { id: MOD_ID(id), label: `${c.name} (Herausforderung)`, effect: c.penalty, endsAt: Infinity });
  bus?.emit('challenge:started', { id });
  bus?.emit('notify', { kind: 'warning', message: `${c.icon} Herausforderung gestartet: ${c.name}` });
  return true;
}

export function abandonChallenge(state: GameState): void {
  const id = state.challenges.activeId;
  if (!id) return;
  state.modifiers = state.modifiers.filter((m) => m.id !== MOD_ID(id));
  state.challenges.activeId = null;
}

/** Tick: auto-complete the active challenge when its target is reached. */
export function tickChallenges(state: GameState, bus?: EventBus): void {
  const id = state.challenges.activeId;
  if (!id) return;
  const c = CHALLENGE_BY_ID[id];
  if (!c) { state.challenges.activeId = null; return; }
  if (challengeProgress(state) >= c.target) {
    state.modifiers = state.modifiers.filter((m) => m.id !== MOD_ID(id));
    state.challenges.activeId = null;
    if (!state.challenges.completed.includes(id)) state.challenges.completed.push(id);
    bus?.emit('challenge:completed', { id });
    bus?.emit('notify', { kind: 'reward', message: `🏅 Herausforderung gemeistert: ${c.name}!` });
  }
}
